import {
  OwcGrid,
  OwcTypography,
  OwcProgressSpinner,
} from "@one/react";
import { useEffect, useState } from "react";
import { chunkArray } from "../../../utils/helpers/text";
import { fetchDocumentationStatus } from "../../../features/equipment-list/DocumentationRender";
import DocumentationModal from "../../../features/equipment-list/DocumentationModal";
import ChecklistFolder from "../../../icons/ChecklistFolder";
const EquipmentDetails = ({ show = true, equipmentData, chunkSize = 6 }) => {
  const [documentationMap, setChecklistMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const bookedEquipments = equipmentData?.bookedEquipments || [];
  const columns = chunkArray(bookedEquipments, chunkSize);

  const truncateText = (text, maxLength = 46) =>
    text && text.length > maxLength ? `${text.substring(0, maxLength - 3)}` : text;

  useEffect(() => {
    if (!equipmentData?.bookingEntries?.length) {
      return;
    }

    const uniqueKeys = [
      ...new Set(
        equipmentData.bookingEntries
          .map((entry) => {
            const equipment = entry.equipment;
            if (!equipment?.siteName || !equipment?.belongingToGroup || !equipment?.equipmentModel) {
              return null;
            }
            return JSON.stringify({ model: equipment.equipmentModel, site: equipment.siteName, group: equipment.belongingToGroup });
          })
          .filter(Boolean)
      ),
    ].map((s) => JSON.parse(s));

    if (!uniqueKeys.length) {
      return;
    }

    setLoading(true);

    Promise.all(
      uniqueKeys.map(async (params) => {
        const items = await fetchDocumentationStatus(params);
        const key = `${params.site}#${params.group}#${params.model}`;
        return [key, items];
      })
    )
      .then((results) => {
        setChecklistMap(Object.fromEntries(results));
      })
      .catch((err) => console.error("Documentation query failed:", err))
      .finally(() => setLoading(false));
  }, [equipmentData]);

  if (!show) {
    return null;
  }

  const handleOpenModal = (documents, shortId) => {
    const foundEntry = equipmentData?.bookingEntries?.find(
      (bookingEntry) => bookingEntry.equipment?.shortIdentifier === shortId
    );
    setSelectedEquipment(foundEntry?.equipment || null);
    setSelectedDocuments(documents);
    setModalOpen(true);
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    setModalOpen(false);
    setSelectedDocuments([]);
    setSelectedEquipment(null);
  };

  return (
    <>
      <OwcTypography variant="button" style={{ margin: "1.125rem 0" }}>
        Equipment details
      </OwcTypography>
      <OwcTypography variant="caption" style={{ marginBottom: "6px" }}>Documentation</OwcTypography>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "120px",
          }}
        >
          <OwcProgressSpinner diameter={60} />
        </div>
      ) : (
        <OwcGrid
          container
          columns={{
            xs: 1,
            s: 1,
            m: 2,
            l: 3,
            xl: 3,
          }}
        >
          {columns.map((column, index) => (
            <OwcGrid
              item
              key={index}
              xl={1}
              l={1}
              m={1}
              xs={1}
              s={1}
              style={{ marginBottom: 12, alignSelf: "start" }}
            >
              {column.map((shortId, itemIndex) => {
                const entry = equipmentData?.bookingEntries?.find(
                  (bookingEntry) => bookingEntry.equipment?.shortIdentifier === shortId
                );
                const equipment = entry?.equipment;
                const key = equipment && `${equipment.siteName}#${equipment.belongingToGroup}#${equipment.equipmentModel}`;
                const documents = key ? documentationMap[key] || [] : [];

                return (
                  <div
                    key={itemIndex}
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {documents.length === 0 ? (
                      <OwcTypography
                        variant="button"
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: "140%",
                          letterSpacing: "0%",
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          fontFamily: "inherit",
                        }}
                      >
                        {truncateText(shortId, 50)}
                      </OwcTypography>
                    ) : (
                      <button
                        onClick={() => handleOpenModal(documents, shortId)}
                        title={`View documentation for ${shortId}`}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <ChecklistFolder size={18} />
                      </button>
                    )}
                  </div>
                );
              })}
            </OwcGrid>
          ))}
        </OwcGrid>
      )}

      <DocumentationModal
        visible={modalOpen}
        onClose={handleCloseModal}
        equipmentDetails={selectedEquipment}
        documents={selectedDocuments}
      />
    </>
  );
};

export default EquipmentDetails;
